/**
 * Register the PWA service worker and surface the "Add to Home Screen" prompt.
 *
 * - Listens for the browser's `beforeinstallprompt` event (Android/Desktop Chrome).
 * - Detects iOS Safari where the event doesn't fire and we have to show
 *   manual "tap Share → Add to Home Screen" instructions instead.
 * - Exposes a tiny event bus the React InstallPrompt component subscribes to.
 */

let deferredPrompt = null;

function emit(name, detail) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

/** Stable localStorage flag — set when the user confirms they've installed the
 *  app (e.g. after the iOS Add-to-Home-Screen steps where Safari can't detect
 *  the install itself). Survives across reloads and tabs. */
const USER_CONFIRMED_KEY = "pct-pwa-confirmed-installed";

export function markUserConfirmedInstalled() {
  try { localStorage.setItem(USER_CONFIRMED_KEY, String(Date.now())); } catch { /* noop */ }
  window.dispatchEvent(new CustomEvent("pwa:installed"));
}

export function clearUserConfirmedInstalled() {
  try { localStorage.removeItem(USER_CONFIRMED_KEY); } catch { /* noop */ }
}

export function isInstalled() {
  // Standalone (installed) display modes — fires whenever the app is opened
  // from a home-screen icon or as an installed desktop PWA.
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true; // iOS
  // App opened with ?source=pwa (set by the admin / portal manifest start_url)
  if (typeof window !== "undefined" && window.location.search.includes("source=pwa")) return true;
  // User explicitly confirmed install (e.g. after iOS Share → Add to Home Screen
  // flow). Survives across regular browser tabs that Safari can't auto-detect.
  try {
    if (localStorage.getItem(USER_CONFIRMED_KEY)) return true;
  } catch { /* ignore */ }
  return false;
}

export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function hasPromptReady() {
  return !!deferredPrompt;
}

export async function triggerInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  deferredPrompt.prompt();
  const result = await deferredPrompt.userChoice;
  deferredPrompt = null;
  emit("pwa:prompt-cleared");
  return result;
}

export function initPWA() {
  // 1. Register service worker
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js", { scope: "/" })
        .then((reg) => {
          // Auto-check for updates when the user returns to the tab
          document.addEventListener("visibilitychange", () => {
            if (!document.hidden) reg.update().catch(() => {});
          });
          // If a waiting worker appears, activate it immediately
          if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
          reg.addEventListener("updatefound", () => {
            const newSW = reg.installing;
            if (!newSW) return;
            newSW.addEventListener("statechange", () => {
              if (newSW.state === "installed" && navigator.serviceWorker.controller) {
                // New version ready
                newSW.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });
        })
        .catch(() => { /* silent — SW is progressive enhancement */ });
    });

    // Reload page when the controlling SW changes (after SKIP_WAITING)
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      // Don't auto-reload while user is mid-action — small delay
      setTimeout(() => window.location.reload(), 100);
    });
  }

  // 2. Capture install prompt (Chrome/Edge/Android)
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit("pwa:prompt-ready");
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    try { localStorage.setItem(USER_CONFIRMED_KEY, String(Date.now())); } catch { /* ignore */ }
    emit("pwa:installed");
  });
}
